import type { ReactNode } from "react";
import { DropdownMenu as R, Tabs as Tb } from "radix-ui";
import { cx } from "../lib/cx";
import { IconCheck } from "./icons";

/* ── DropdownMenu ───────────────────────────────────────────────── */

export const Menu = R.Root;
export const MenuTrigger = R.Trigger;

export function MenuContent({
  children,
  align = "start",
  side = "bottom",
  className,
  sideOffset = 6,
  onCloseAutoFocus,
}: {
  children: ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  sideOffset?: number;
  /** Where focus goes when it closes; preventDefault() when an item opened a dialog, so the dialog keeps it. */
  onCloseAutoFocus?: (e: Event) => void;
}) {
  return (
    <R.Portal>
      <R.Content
        align={align}
        side={side}
        sideOffset={sideOffset}
        onCloseAutoFocus={onCloseAutoFocus}
        collisionPadding={8}
        // A long menu scrolls inside the room the viewport has, instead of running off its edge.
        className={cx("hx-pop pop z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[220px] overflow-y-auto p-1.5", className)}
      >
        {children}
      </R.Content>
    </R.Portal>
  );
}

const itemClass =
  "relative flex min-h-9 cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[14px] text-body outline-none " +
  "data-[highlighted]:bg-panel data-[highlighted]:text-fg data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

export function MenuItem({
  children,
  icon,
  onSelect,
  danger,
  shortcut,
  disabled,
  className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  onSelect?: (e: Event) => void;
  danger?: boolean;
  shortcut?: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    // "!" because itemClass's text-body sorts after text-bad in the stylesheet and would win.
    <R.Item onSelect={onSelect} disabled={disabled} className={cx(itemClass, danger && "text-bad! data-[highlighted]:text-bad", className)}>
      {icon ? <span className={danger ? "text-bad" : "text-dim"}>{icon}</span> : null}
      <span className="min-w-0 flex-1">{children}</span>
      {shortcut ? <kbd className="font-mono text-[11px] text-dim">{shortcut}</kbd> : null}
    </R.Item>
  );
}

export function MenuRadioGroup({ value, onValueChange, children }: { value: string; onValueChange: (v: string) => void; children: ReactNode }) {
  return (
    <R.RadioGroup value={value} onValueChange={onValueChange}>
      {children}
    </R.RadioGroup>
  );
}

/** A choice of one; the menu stays open when it's chosen, unless `closes` (a pick that's the whole point of the menu). */
export function MenuRadioItem({ value, children, icon, closes, disabled }: { value: string; children: ReactNode; icon?: ReactNode; closes?: boolean; disabled?: boolean }) {
  return (
    <R.RadioItem value={value} disabled={disabled} className={itemClass} onSelect={closes ? undefined : (e) => e.preventDefault()}>
      {icon ? <span className="text-dim">{icon}</span> : null}
      <span className="min-w-0 flex-1">{children}</span>
      <R.ItemIndicator>
        <IconCheck size={15} className="text-accent" />
      </R.ItemIndicator>
    </R.RadioItem>
  );
}

/** A menu item that's on or off (a check when on); the menu stays open when it's chosen. */
export function MenuCheckboxItem({ checked, onCheckedChange, children }: { checked: boolean; onCheckedChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <R.CheckboxItem checked={checked} onCheckedChange={(v) => onCheckedChange(v === true)} onSelect={(e) => e.preventDefault()} className={itemClass}>
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] shadow-[inset_0_0_0_1.5px_var(--hx-line-strong)] data-[state=checked]:bg-brand-600" data-state={checked ? "checked" : "unchecked"}>
        <R.ItemIndicator>
          <IconCheck size={12} strokeWidth={3} className="text-white" />
        </R.ItemIndicator>
      </span>
      <span className="min-w-0 flex-1">{children}</span>
    </R.CheckboxItem>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <R.Label className="kicker px-2.5 pt-2 pb-1.5">{children}</R.Label>;
}

export function MenuSeparator() {
  return <R.Separator className="-mx-1.5 my-1.5 h-px bg-line" />;
}

/* ── Tooltip ────────────────────────────────────────────────────── */

// The tooltip lives in ui/Tooltip.tsx (hover, focus and tap); these keep older imports working.
export { Tooltip, TooltipProvider } from "./Tooltip";

/* ── Tabs ───────────────────────────────────────────────────────── */

export const Tabs = Tb.Root;
export const TabsContent = Tb.Content;

export function TabsList({ children, label, className }: { children: ReactNode; label: string; className?: string }) {
  return (
    <Tb.List
      aria-label={label}
      className={cx(
        "inline-flex max-w-full gap-0.5 overflow-x-auto rounded-full bg-surface p-1 shadow-[0_0_0_1px_var(--hx-line),0_8px_20px_-14px_rgba(15,23,42,.25)]",
        className,
      )}
    >
      {children}
    </Tb.List>
  );
}

export function TabsTrigger({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return (
    <Tb.Trigger
      value={value}
      className={cx(
        "inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium whitespace-nowrap text-body transition-colors duration-150",
        "hover:bg-panel hover:text-fg data-[state=active]:bg-brand-solid data-[state=active]:text-on-brand data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,0.14)]",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-500",
        className,
      )}
    >
      {children}
    </Tb.Trigger>
  );
}
